import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { BLOGS } from "../data/blogsData";

/* ── Section renderer ── */
function ArticleSection({ section }) {
  switch (section.type) {
    case "h2":
      return <h2 className="bp-h2" dangerouslySetInnerHTML={{ __html: section.content }} />;
    case "h3":
      return <h3 className="bp-h3" dangerouslySetInnerHTML={{ __html: section.content }} />;
    case "paragraph":
      return <p className="bp-p" dangerouslySetInnerHTML={{ __html: section.content }} />;
    case "pullquote":
      return <blockquote className="bp-pullquote" dangerouslySetInnerHTML={{ __html: section.content }} />;
    case "callout":
      return (
        <div
          className="bp-callout"
          dangerouslySetInnerHTML={{ __html: section.content }}
        />
      );
    default:
      return null;
  }
}

/* ── FAQ Accordion ── */
function FAQ({ items }) {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section className="bp-faq" aria-label="Frequently asked questions">
      <h2 className="bp-faq__title">Frequently asked questions</h2>
      {items.map((item, i) => {
        const isOpen = openIdx === i;
        return (
          <div
            key={i}
            className={`bp-faq__item${isOpen ? " bp-faq__item--open" : ""}`}
          >
            <button
              className="bp-faq__q"
              aria-expanded={isOpen}
              onClick={() => setOpenIdx(isOpen ? null : i)}
              id={`faq-q-${i}`}
              aria-controls={`faq-a-${i}`}
            >
              {item.q}
              <svg
                className="bp-faq__icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <div
              id={`faq-a-${i}`}
              role="region"
              aria-labelledby={`faq-q-${i}`}
              className="bp-faq__a"
            >
              {item.a}
            </div>
          </div>
        );
      })}
    </section>
  );
}

/* ── Related Posts ── */
// function RelatedPosts({ currentSlug }) {
//   const related = BLOGS.filter((b) => b.slug !== currentSlug).slice(0, 3);
//   if (related.length === 0) return null;
//   return (
//     <section className="bp-related">
//       <h2 className="bp-related__title">More Insights</h2>
//       <div className="bp-related__grid">
//         {related.map((blog) => (
//           <Link key={blog.slug} to={`/blog/${blog.slug}`} className="bp-related__card">
//             <div className="bp-related__img-wrap">
//               <img
//                 src={blog.image}
//                 alt={blog.imageAlt}
//                 className="bp-related__img"
//                 loading="lazy"
//                 onError={(e) => { e.target.style.display = "none"; }}
//               />
//               <span className="bp-related__cat">{blog.category}</span>
//             </div>
//             <div className="bp-related__body">
//               <p className="bp-related__date">{blog.dateDisplay} · {blog.readTime}</p>
//               <h3 className="bp-related__heading">{blog.title}</h3>
//             </div>
//           </Link>
//         ))}
//       </div>
//     </section>
//   );
// }

/* ── Main Page ── */
export default function BlogPost() {
  const { slug } = useParams();
  const blog = BLOGS.find((b) => b.slug === slug);

  // 404 redirect if slug not found
  if (!blog) return <Navigate to="/blog" replace />;

  const siteUrl = "https://www.novacapitaldxb.com";
  const canonicalUrl = `${siteUrl}/blog/${blog.slug}`;

  // JSON-LD for BlogPosting
  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": blog.title,
        "description": blog.metaDescription,
        "image": `${siteUrl}${blog.image}`,
        "datePublished": blog.datePublished,
        "dateModified": blog.datePublished,
        "author": {
          "@type": "Organization",
          "name": blog.author,
          "url": siteUrl,
        },
        "publisher": {
          "@type": "Organization",
          "name": "Nova Capital Real Estate",
          "logo": {
            "@type": "ImageObject",
            "url": `${siteUrl}/logo.png`,
          },
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": canonicalUrl,
        },
        "keywords": blog.keywords,
        "articleSection": blog.category,
        "inLanguage": "en-AE",
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "Insights", "item": `${siteUrl}/blog` },
          { "@type": "ListItem", "position": 3, "name": blog.title, "item": canonicalUrl },
        ],
      },
      ...(blog.faqSchema
        ? [{
          "@type": "FAQPage",
          "mainEntity": blog.faqSchema.map((f) => ({
            "@type": "Question",
            "name": f.question,
            "acceptedAnswer": { "@type": "Answer", "text": f.answer },
          })),
        }]
        : []),
    ],
  };

  return (
    <>
      {/* ═══════════════ FULL SEO ═══════════════ */}
      <Helmet>
        <title>{blog.title} | Nova Capital Real Estate</title>
        <meta name="description" content={blog.metaDescription} />
        <meta name="keywords" content={blog.keywords} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Nova Capital Real Estate" />
        <meta property="og:locale" content="en_AE" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.ogDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${siteUrl}${blog.image}`} />
        <meta property="article:published_time" content={blog.datePublished} />
        <meta property="article:author" content={blog.author} />
        <meta property="article:section" content={blog.category} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.ogDescription} />
        <meta name="twitter:image" content={`${siteUrl}${blog.image}`} />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(blogPostingSchema)}</script>
      </Helmet>

      <Navbar />


      <main className="bp-main">
        <article
          className="bp-article"
          itemScope
          itemType="https://schema.org/BlogPosting"
        >
          {/* ── Breadcrumb ── */}
          <nav className="bp-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">›</span>
            <Link to="/blogs">Blogs</Link>
            <span aria-hidden="true">›</span>
            <span>{blog.category}</span>
          </nav>

          {/* ── Category tag ── */}
          <span className="bp-tag">{blog.tag}</span>

          {/* ── H1 ── */}
          <h1 className="bp-h1" itemProp="headline">{blog.title}</h1>

          {/* ── Byline ── */}
          <div className="bp-byline">
            <strong itemProp="author">{blog.author}</strong>
            <span className="bp-byline__dot">·</span>
            <time itemProp="datePublished" dateTime={blog.datePublished}>
              {blog.dateDisplay}
            </time>
            <span className="bp-byline__dot">·</span>
            <span>{blog.readTime}</span>
          </div>

          {/* ── Hero image ── */}
          <figure className="bp-hero">
            <img
              src={blog.image}
              alt={blog.imageAlt}
              width="1200"
              height="525"
              loading="eager"
              itemProp="image"
              className="bp-hero__img"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            {blog.imageCaption && (
              <figcaption className="bp-hero__caption">{blog.imageCaption}</figcaption>
            )}
          </figure>

          {blog.html ? <div className="raw-html-blog" dangerouslySetInnerHTML={{ __html: blog.html }} /> : <>
            {blog.stats && (
              <div className="bp-stats" role="region" aria-label="Key market statistics">
                {blog.stats.map((s, i) => (
                  <div key={i} className="bp-stat">
                    <span className="bp-stat__num">{s.num}</span>
                    <span className="bp-stat__label">{s.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* ── Article body ── */}
            <div className="bp-prose" itemProp="articleBody">
              {blog.sections.map((sec, i) => (
                <ArticleSection key={i} section={sec} />
              ))}
            </div>

            {/* ── FAQ ── */}
            {blog.faqs && <FAQ items={blog.faqs} />}
          </>}

          {/* ── CTA ── */}
          <div className="bp-cta">
            <p className="bp-cta__eyebrow">Zero Commission · Zero Pressure · Zero Guesswork</p>
            <h2 className="bp-cta__heading">Speak to Nova Capital before you decide</h2>
            <p className="bp-cta__sub">
              We have been operating in Dubai's market through every cycle. Whether you are an NRI buyer
              in India, a UK expat, or an international investor assessing your options, our advisors can
              give you an honest, data-driven view of which properties make sense right now.
            </p>
            <div className="bp-cta__btns">
              <Link to="/contact" className="bp-btn bp-btn--primary" id="blog-cta-contact">
                Contact Nova Capital
              </Link>
              {/* <a
                href="https://wa.me/971000000000"
                className="bp-btn bp-btn--outline"
                rel="noopener noreferrer"
                target="_blank"
                id="blog-cta-whatsapp"
              >
                WhatsApp Us
              </a> */}
            </div>
          </div>
        </article>

        {/* ── Related posts ── */}
        {/* <div className="bp-related-wrap">
          <RelatedPosts currentSlug={slug} />
        </div> */}
      </main>

      <Footer />

      <style>{`
        /* ═══════════════ BLOG POST STYLES ═══════════════ */
        :root {
          --nc-gold: #C9A84C;
          --nc-gold-light: #b78800;
          // --nc-dark: #0F1117;
          // --nc-charcoal: #1C1F28;
          // --nc-mid: #2B2F3A;
          // --nc-text: #E8E6E1;
          // --nc-muted: #9A97A0;
          // --nc-border: rgba(201,168,76,0.18);
          --nc-dark: #F7F9FC;
            --nc-charcoal: #FFFFFF;
            --nc-mid: #EBF0F6;
            --nc-text: #1C1F28;
            --nc-muted: rgba(100, 116, 139, 1);
            --nc-border: rgba(201, 168, 76, 0.3);
          --nc-radius: 6px;
          --nc-font-display: 'Georgia', 'Times New Roman', serif;
          --nc-max-w: 780px;
        }

        /* ── Light Mode Override ── */
        @media (prefers-color-scheme: light) {
          :root {
            --nc-dark: #F7F9FC;
            --nc-charcoal: #FFFFFF;
            --nc-mid: #EBF0F6;
            --nc-text: #1C1F28;
            --nc-muted: #64748B;
            --nc-border: rgba(201, 168, 76, 0.3);
          }
        }

        .light-mode,
        .light,
        [data-theme='light'] {
          --nc-dark: #F7F9FC;
          --nc-charcoal: #FFFFFF;
          --nc-mid: #EBF0F6;
          --nc-text: #1C1F28;
          --nc-muted: #64748B;
          --nc-border: rgba(201, 168, 76, 0.3);
        }

        .bp-main {
          background: var(--nc-dark);
          color: var(--nc-text);
          font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
          font-size: 17px;
          line-height: 1.75;
          -webkit-font-smoothing: antialiased;
          padding-top: 70px;
        }

        .bp-article {
          max-width: var(--nc-max-w);
          margin: 0 auto;
          padding: 60px 24px 80px;
        }

        /* ── Breadcrumb ── */
        .bp-breadcrumb {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--nc-muted);
          margin-bottom: 32px;
        }
        .bp-breadcrumb a {
          color: var(--nc-gold);
          text-decoration: none;
        }
        .bp-breadcrumb a:hover { text-decoration: underline; }

        /* ── Tag ── */
        .bp-tag {
          display: inline-block;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--nc-gold);
          border: 1px solid var(--nc-gold);
          border-radius: 2px;
          padding: 3px 10px;
          margin-bottom: 20px;
        }

        /* ── H1 ── */
        .bp-h1 {
          font-family: var(--nc-font-display);
          font-size: clamp(26px, 4vw, 40px);
          font-weight: 400;
          line-height: 1.22;
          color: var(--nc-text);
          margin-bottom: 20px;
        }

        /* ── Byline ── */
        .bp-byline {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          font-size: 13px;
          color: var(--nc-muted);
          padding-bottom: 28px;
          border-bottom: 1px solid var(--nc-border);
          margin-bottom: 40px;
        }
        .bp-byline strong { color: var(--nc-text); }
        .bp-byline__dot { color: rgba(201,168,76,0.35); }

        /* ── Hero ── */
        .bp-hero {
          width: 100%;
          aspect-ratio: 16/7;
          background: var(--nc-mid);
          border-radius: var(--nc-radius);
          overflow: hidden;
          margin-bottom: 48px;
          position: relative;
        }
        .bp-hero__img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .bp-hero__caption {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          background: linear-gradient(transparent, rgba(0,0,0,0.7));
          font-size: 12px;
          color: rgba(255,255,255,0.65);
          padding: 24px 16px 10px;
          text-align: center;
        }

        /* ── Stats ── */
        .bp-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1px;
          background: var(--nc-border);
          border: 1px solid var(--nc-border);
          border-radius: var(--nc-radius);
          overflow: hidden;
          margin-bottom: 48px;
        }
        .bp-stat {
          background: var(--nc-charcoal);
          padding: 20px 18px;
          text-align: center;
        }
        .bp-stat__num {
          font-family: var(--nc-font-display);
          font-size: 26px;
          color: var(--nc-gold);
          display: block;
          line-height: 1;
          margin-bottom: 6px;
        }
        .bp-stat__label {
          font-size: 12px;
          color: var(--nc-muted);
          line-height: 1.35;
        }

        /* ── Prose ── */
        .bp-h2 {
          font-family: var(--nc-font-display);
          font-size: clamp(20px, 2.5vw, 26px);
          font-weight: 400;
          color: var(--nc-text);
          margin: 52px 0 16px;
          padding-bottom: 10px;
          border-bottom: 1px solid var(--nc-border);
        }
        .bp-h3 {
          font-size: 17px;
          font-weight: 600;
          color: var(--nc-gold-light);
          margin: 32px 0 10px;
          letter-spacing: 0.02em;
        }
        .bp-p {
          margin-bottom: 20px;
          color: var(--nc-muted);
        }
        .bp-prose a {
          color: var(--nc-gold);
          text-decoration: underline;
          text-underline-offset: 4px;
          font-weight: 500;
          transition: color 0.2s ease;
        }
        .bp-prose a:hover {
          color: var(--nc-gold-light);
        }
        .bp-pullquote {
          border-left: 3px solid var(--nc-gold);
          margin: 40px 0;
          padding: 16px 24px;
          background: var(--nc-charcoal);
          border-radius: 0 var(--nc-radius) var(--nc-radius) 0;
          font-family: var(--nc-font-display);
          font-size: 19px;
          font-style: italic;
          color: var(--nc-gold-light);
          line-height: 1.5;
        }
        .bp-callout {
          background: var(--nc-charcoal);
          border: 1px solid var(--nc-border);
          border-radius: var(--nc-radius);
          padding: 20px 24px;
          margin: 32px 0;
          font-size: 15px;
          color: var(--nc-muted);
        }
        .bp-callout strong { color: var(--nc-text); }

        /* ── FAQ ── */
        .bp-faq { margin-top: 56px; }
        .bp-faq__title {
          font-family: var(--nc-font-display);
          font-size: 22px;
          font-weight: 400;
          color: var(--nc-text);
          margin-bottom: 24px;
          padding-bottom: 10px;
          border-bottom: 1px solid var(--nc-border);
        }
        .bp-faq__item {
          border-bottom: 1px solid var(--nc-border);
        }
        .bp-faq__q {
          width: 100%;
          background: none;
          border: none;
          text-align: left;
          color: var(--nc-text);
          font-size: 16px;
          font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
          font-weight: 500;
          padding: 18px 0;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          transition: color 0.2s;
        }
        .bp-faq__q:hover { color: var(--nc-gold-light); }
        .bp-faq__icon {
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          color: var(--nc-gold);
          transition: transform 0.25s ease;
        }
        .bp-faq__item--open .bp-faq__icon {
          transform: rotate(45deg);
        }
        .bp-faq__a {
          font-size: 15px;
          color: var(--nc-muted);
          line-height: 1.7;
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.35s ease, padding 0.25s ease;
          padding: 0;
        }
        .bp-faq__item--open .bp-faq__a {
          max-height: 400px;
          padding-bottom: 20px;
        }

        /* ── CTA ── */
        .bp-cta {
          margin-top: 64px;
          padding: 44px 36px;
          background: linear-gradient(135deg, var(--nc-charcoal) 0%, var(--nc-mid) 100%);
          border: 1px solid var(--nc-gold);
          border-radius: var(--nc-radius);
          text-align: center;
        }
        .bp-cta__eyebrow {
          font-size: 11px;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: var(--nc-gold);
          margin-bottom: 14px;
        }
        .bp-cta__heading {
          font-family: var(--nc-font-display);
          font-size: clamp(20px, 3vw, 28px);
          font-weight: 400;
          color: var(--nc-text);
          margin-bottom: 12px;
        }
        .bp-cta__sub {
          font-size: 15px;
          color: var(--nc-muted);
          max-width: 600px;
          margin: 0 auto 28px;
          line-height: 1.65;
        }
        .bp-cta__btns {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .bp-btn {
          display: inline-block;
          padding: 13px 30px;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.04em;
          border-radius: 3px;
          text-decoration: none;
          transition: opacity 0.15s, transform 0.15s;
        }
        .bp-btn:hover { opacity: 0.85; transform: translateY(-1px); }
        .bp-btn--primary {
          background: var(--nc-gold);
          color: var(--nc-dark);
        }
        .bp-btn--outline {
          border: 1px solid var(--nc-gold);
          color: var(--nc-gold);
        }

        /* ── Related ── */
        .bp-related-wrap {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 24px 80px;
        }
        .bp-related__title {
          font-family: var(--nc-font-display);
          font-size: 24px;
          font-weight: 400;
          color: #fff;
          margin-bottom: 28px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--nc-border);
        }
        .bp-related__grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
        }
        .bp-related__card {
          background: var(--nc-charcoal);
          border: 1px solid var(--nc-border);
          border-radius: var(--nc-radius);
          overflow: hidden;
          text-decoration: none;
          display: flex;
          flex-direction: column;
          transition: transform 0.2s ease, border-color 0.2s ease;
        }
        .bp-related__card:hover {
          transform: translateY(-4px);
          border-color: rgba(201,168,76,0.4);
        }
        .bp-related__img-wrap {
          position: relative;
          aspect-ratio: 16/9;
          background: var(--nc-mid);
          overflow: hidden;
        }
        .bp-related__img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        .bp-related__card:hover .bp-related__img {
          transform: scale(1.04);
        }
        .bp-related__cat {
          position: absolute;
          top: 10px;
          left: 10px;
          background: var(--nc-dark);
          color: var(--nc-gold);
          border: 1px solid var(--nc-gold);
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          padding: 2px 8px;
          border-radius: 2px;
        }
        .bp-related__body {
          padding: 18px;
        }
        .bp-related__date {
          font-size: 12px;
          color: var(--nc-muted);
          margin-bottom: 8px;
        }
        .bp-related__heading {
          font-family: var(--nc-font-display);
          font-size: 16px;
          font-weight: 400;
          color: #fff;
          line-height: 1.35;
          margin: 0;
        }
        .bp-related__card:hover .bp-related__heading {
          color: var(--nc-gold-light);
        }

        /* ── Responsive ── */
        @media (max-width: 600px) {
          .bp-article { padding: 36px 18px 60px; }
          .bp-cta { padding: 28px 20px; }
          .bp-related-wrap { padding: 0 18px 60px; }
        }
      `}</style>
    </>
  );
}
